<template>
  <v-card>
    <v-card-title class="toolbar">
      <h3>Orders ({{ filteredItems.length }})</h3>
      <span v-if="account">
        <span
          v-if="account.label"
          class="white--text infoLabel"
        >{{ account.label }}/</span>
        <span class="white--text infoLabel">
          <strong>{{ account.name }}</strong>
        </span>
      </span>
      <span class="white--text infoLabel">[Account ID:{{ accountId }}]</span>
      <v-spacer />
      <v-text-field
        v-model="search"
        dark
        append-icon="search"
        label="Search"
        single-line
        hide-details
      />
    </v-card-title>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :search="search"
      select-all
      item-key="name"
      class="elevation-1"
      @update:pagination="updatePagination"
    >
      <template
        slot="headers"
        slot-scope="props"
      >
        <tr>
          <th>
            <v-checkbox
              :input-value="props.all"
              :indeterminate="props.indeterminate"
              primary
              hide-details
              @click.native="toggleAll"
            />
          </th>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="['column sortable', pagination.descending ? 'desc' : 'asc',
                     header.value === pagination.sortBy ? 'active' : '']"
            @click="changeSort(header.value)"
          >
            <v-icon small>
              arrow_upward
            </v-icon>
            {{ header.text }}
          </th>
        </tr>
        <tr class="grey lighten-3">
          <th>
            <v-icon>filter_list</v-icon>
          </th>
          <th
            v-for="header in props.headers"
            :key="header.text"
          >
            <div v-if="header.filter && header.filter.value">
              <div v-if="header.filter.type=='select'">
                <v-select
                  v-model="filters[header.value]"
                  flat
                  hide-details
                  small
                  multiple
                  clearable
                  :items="columnValueList(header.value, header.type)"
                />
              </div>
              <div v-if="header.filter.type=='input'">
                <v-text-field
                  v-model="filters[header.value]"
                  @change="columnValueList(header.value, header.type)"
                />
              </div>
            </div>
          </th>
        </tr>
      </template>
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          :active="props.selected"
          @click="props.selected = !props.selected"
        >
          <td>
            <v-checkbox
              :input-value="props.selected"
              primary
              hide-details
            />
          </td>
          <td v-if="!accountId">
            {{ props.item.accountName }}
          </td>
          <td class="text-xs-center">
            {{ props.item.name }}
          </td>
          <td class="text-xs-center">
            {{ props.item.orderRef }}
          </td>
          <td class="text-xs-center">
            {{ props.item.amount | currency('€') }}
          </td>
          <td class="text-xs-center">
            {{ props.item.status }}
          </td>
          <td class="text-xs-center">
            {{ props.item.type }}
          </td>
          <td class="text-xs-center">
            {{ formatDate(props.item.createdAt) }}
          </td>
          <td class="text-xs-center">
            {{ props.item.age }}
          </td>
          <router-link :to="{name: 'order_detail', params: {id: props.item.id}}">
            <v-btn
              flat
              color="blue"
            >
              <v-icon>visibility</v-icon>
            </v-btn>
          </router-link>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { setQuery } from '@/helpers';

export default {
  name: 'OrdersTable',
  props: {
    orders: {
      type: Array,
      default: () => ([]),
    },
    account: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      search: '',
      selected: [],
      pagination: {
        page: 1,
        rowsPerPage: 15,
        totalItems: 0,
        sortBy: 'createdAt',
        descending: true,
      },
      filters: {
        createdAt: [],
        type: [],
        status: [],
        age: '',
      },
      headers: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Order Ref',
          align: 'left',
          sortable: true,
          value: 'orderRef',
        },
        { text: 'Amount', value: 'amount' },
        {
          text: 'Status',
          value: 'status',
          filter: { value: true, type: 'select' },
        },
        {
          text: 'Type',
          value: 'type',
          filter: { value: true, type: 'select' },
        },
        { text: 'Created at', value: 'createdAt' },
        {
          text: 'Age (days old)',
          value: 'age',
          filter: { value: true, type: 'input' },
          type: 'age',
        },
        { text: 'Actions', value: 'actions' },
      ],
      isLoadTableData: false,
    };
  },
  computed: {
    accountId() {
      return this.$route.query.accountId;
    },
    filteredItems() {
      return this.orders.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          if (f === 'age') {
            return String(this.filters[f]) === d[f];
          }

          return (
            this.filters[f].length < 1 ||
              String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
  },
  watch: {
    orders() {
      this.getDefaultFilters();
    },
    search(val) {
      setQuery({ search: val }, this.$route, this.$router);
    },
  },
  async mounted() {
    if (!this.accountId) {
      this.headers.unshift({
        text: 'Account name',
        align: 'left',
        sortable: true,
        value: 'name',
      });
    }

    await this.$nextTick();
    this.changePage();
  },
  methods: {
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    toggleAll() {
      if (this.selected.length) {
        this.selected = [];
      } else {
        this.selected = this.orders.slice();
      }
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    columnValueList(val) {
      return this.orders.map(d => d[val]);
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    updatePagination(pagination) {
      if (this.isLoadTableData) {
        setQuery({ page: pagination.page }, this.$route, this.$router);
      }
    },
    async changePage() {
      const { page, search } = this.$route.query;
      this.search = search;
      await this.$nextTick();
      this.isLoadTableData = true;
      this.pagination.page = page ? Number(page) : 1;
    },
  },
};
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
  color: black;
}
.toolbar {
  background-color: #1a4c7d;
  color: white;
}
.infoLabel {
  margin-left: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/orders/OrdersTable.vue